// src/geoArea.js
// Spherical polygon area and centroid, defined ONCE.
//
// This is a straight extraction of the body of `_geomCentroidAcres` in
// src/App.jsx — same formula, same radius, same divisor — so that the PRF grid
// exposure calculation can measure acres the same way the Field Mapper already
// does. It is an extraction rather than a new implementation on purpose: a
// second acreage formula in this repo is a second answer to "how big is this
// field", and the two would drift the first time either was touched.
//
// ⚠ THE RING AREA IS SPHERICAL, NOT PLANAR, AND THAT IS LOAD-BEARING AT THIS
// LATITUDE. A planar shoelace on raw degrees treats a degree of longitude as a
// degree of latitude; at 43°N a degree of longitude is cos(43°) = 0.73 of one,
// so a planar area runs ~37% high. The spherical-excess form below carries the
// latitude term (`sin(p1.lat) + sin(p2.lat)`) and needs no projection.
//
// The centroid IS planar (shoelace on degrees) and is deliberately left that
// way: it is used to place a marker and to pick a grid, where a few metres of
// offset changes nothing, and it matches what App.jsx has always returned.

const EARTH_R = 6378137;          // WGS84 semi-major axis, metres
const DEG = Math.PI / 180;
const M2_PER_ACRE = 4046.8564224;

// Signed area of one ring in m². Sign carries winding, which is what lets a
// hole be subtracted from its shell.
function ringAreaM2(ring) {
  let a = 0;
  for (let i = 0, n = ring.length; i < n; i++) {
    const p1 = ring[i], p2 = ring[(i + 1) % n];
    a += (p2[0] - p1[0]) * DEG * (2 + Math.sin(p1[1] * DEG) + Math.sin(p2[1] * DEG));
  }
  return a * EARTH_R * EARTH_R / 2;
}

// GeoJSON Polygon / MultiPolygon → m², holes subtracted. Anything else → null,
// never 0: "not a polygon" and "zero acres" are different answers and only one
// of them may be summed.
function geomAreaM2(geom) {
  if (!geom) return null;
  const polys = geom.type === "MultiPolygon" ? geom.coordinates
    : geom.type === "Polygon" ? [geom.coordinates] : null;
  if (!polys) return null;
  let m2 = 0;
  for (const rings of polys) {
    if (!rings || !rings[0] || rings[0].length < 3) continue;
    let polyArea = Math.abs(ringAreaM2(rings[0]));
    for (let h = 1; h < rings.length; h++) polyArea -= Math.abs(ringAreaM2(rings[h]));
    m2 += polyArea;
  }
  return m2;
}

function geomAcres(geom) {
  const m2 = geomAreaM2(geom);
  return m2 == null ? null : m2 / M2_PER_ACRE;
}

// The full result App.jsx has always returned: planar centroid weighted by each
// polygon's spherical area, plus the acreage.
function geomCentroidAcres(geom) {
  if (!geom) return null;
  const polys = geom.type === "MultiPolygon" ? geom.coordinates
    : geom.type === "Polygon" ? [geom.coordinates] : null;
  if (!polys) return null;
  let m2 = 0, cx = 0, cy = 0, wsum = 0;
  for (const rings of polys) {
    if (!rings || !rings[0] || rings[0].length < 3) continue;
    let polyArea = Math.abs(ringAreaM2(rings[0]));
    for (let h = 1; h < rings.length; h++) polyArea -= Math.abs(ringAreaM2(rings[h]));
    m2 += polyArea;
    const ring = rings[0]; let A = 0, x = 0, y = 0;
    for (let i = 0, n = ring.length; i < n; i++) {
      const p1 = ring[i], p2 = ring[(i + 1) % n];
      const cross = p1[0] * p2[1] - p2[0] * p1[1];
      A += cross; x += (p1[0] + p2[0]) * cross; y += (p1[1] + p2[1]) * cross;
    }
    if (A !== 0) { const w = Math.abs(polyArea); cx += (x / (3 * A)) * w; cy += (y / (3 * A)) * w; wsum += w; }
  }
  if (wsum <= 0) return null;
  return { lon: cx / wsum, lat: cy / wsum, acres: m2 / M2_PER_ACRE };
}

module.exports = { ringAreaM2, geomAreaM2, geomAcres, geomCentroidAcres, EARTH_R, M2_PER_ACRE };
